import { Modal, TextInput, Accordion, PasswordInput, Select, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { LoadingButton } from '@mui/lab';
import { useEffect, useMemo } from 'react';

interface UserModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: any) => void;
  isSuperAdmin: boolean;
  initialValues?: any;
  isLoading: boolean;
}

export function UserModal({
  opened,
  onClose,
  onSubmit,
  isSuperAdmin,
  initialValues,
  isLoading,
}: UserModalProps) {
  const emptyValues = useMemo(
    () => ({
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      isSuperAdmin: false,
      canAccessInternalTools: false,
      password: '',
      confirmedPassword: '',
    }),
    [],
  );

  const isEdit = !!initialValues;

  const form = useForm({
    initialValues: emptyValues,
    validate: {
      email: (value: any) => {
        if (!value?.trim()) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email format';
        return null;
      },
      firstName: (value: any) => (!value?.trim() ? 'First name is required' : null),
      lastName: (value: any) => (!value?.trim() ? 'Last name is required' : null),
      password: (value: any) => {
        if (!isEdit) {
          if (!value || value.length < 8) return 'Password must be at least 8 characters';
        } else if (value && value.length < 8) {
          return 'Password must be at least 8 characters';
        }
        return null;
      },
      confirmedPassword: (value: any, values: any) => {
        if (!isEdit) {
          if (!value) return 'Password confirmation is required';
          if (value !== values.password) return 'Passwords do not match';
          return null;
        }
        if (values.password) {
          if (!value) return 'Password confirmation is required';
          if (value !== values.password) return 'Passwords do not match';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (!opened) return;

    const next = isEdit
      ? {
        id: initialValues?.id ?? '',
        firstName: initialValues?.firstName ?? '',
        lastName: initialValues?.lastName ?? '',
        email: initialValues?.email ?? '',
        isSuperAdmin: !!initialValues?.isSuperAdmin,
        canAccessInternalTools: !!initialValues?.canAccessInternalTools,
        password: '',
        confirmedPassword: '',
      }
      : emptyValues;

    form.setValues(next);
    form.setInitialValues(next);
    form.resetDirty(next);
    form.clearErrors();
  }, [opened, isEdit, initialValues?.id, emptyValues]);

  return (
    <Modal
      key={initialValues?.id ?? 'new'}
      opened={opened}
      onClose={() => {
        form.setValues(emptyValues);
        form.setInitialValues(emptyValues);
        form.resetDirty(emptyValues);
        form.clearErrors();
        onClose();
      }}
      title={isEdit ? 'Edit User' : 'Add User'}
      centered
    >
      <form
        key={isEdit ? `edit-${initialValues?.id ?? ''}` : 'add'}
        onSubmit={form.onSubmit((values) => onSubmit(values))}
      >
        <TextInput
          withAsterisk
          label="Email"
          key={form.key('email')}
          {...form.getInputProps('email')}
        />

        <TextInput
          label="First Name"
          withAsterisk
          key={form.key('firstName')}
          {...form.getInputProps('firstName')}
        />

        <TextInput
          label="Last Name"
          withAsterisk
          key={form.key('lastName')}
          {...form.getInputProps('lastName')}
        />

        {isSuperAdmin && (
          <Select
            label="Role"
            withAsterisk
            key={form.key('isSuperAdmin')}
            data={[
              { value: 'Admin', label: 'Admin' },
              { value: 'User', label: 'User' },
            ]}
            value={form.values.isSuperAdmin ? 'Admin' : 'User'}
            onChange={(v) => form.setFieldValue('isSuperAdmin', v === 'Admin')}
          />
        )}

        {isSuperAdmin && (
          <Select
            label="Internal Tools Access"
            key={form.key('canAccessInternalTools')}
            data={[
              { value: 'true', label: 'Enabled' },
              { value: 'false', label: 'Disabled' },
            ]}
            value={form.values.canAccessInternalTools ? 'true' : 'false'}
            onChange={(value) => form.setFieldValue('canAccessInternalTools', value === 'true')}
          />
        )}

        {!isEdit ? (
          <>
            <PasswordInput
              withAsterisk
              label="Password"
              key={form.key('password')}
              {...form.getInputProps('password')}
              autoComplete="new-password"
            />
            <PasswordInput
              withAsterisk
              label="Confirm New Password"
              key={form.key('confirmedPassword')}
              {...form.getInputProps('confirmedPassword')}
              autoComplete="new-password"
            />
          </>
        ) : (
          <Accordion variant="contained" mt="lg">
            <Accordion.Item value="password-fields">
              <Accordion.Control>
                <span style={{ fontSize: 14, fontWeight: 500 }}>Change Password</span>
              </Accordion.Control>
              <Accordion.Panel>
                <PasswordInput
                  label="Password"
                  key={form.key('password')}
                  {...form.getInputProps('password')}
                  autoComplete="new-password"
                />
                <PasswordInput
                  label="Confirm New Password"
                  key={form.key('confirmedPassword')}
                  {...form.getInputProps('confirmedPassword')}
                  autoComplete="new-password"
                />
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        )}

        <Group justify="flex-end" mt="md">
          <LoadingButton type="submit" loading={isLoading} variant="contained" color="primary">
            Save
          </LoadingButton>
        </Group>
      </form>
    </Modal>
  );
}
